import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { KPI_DEFINITIONS, getStatusColor } from "../utils/kpiDefinitions";
import { colors, shadows, radius, spacing, typography, transitions, zIndex } from "../utils/designTokens";

/**
 * Tooltip explicativo para KPIs financieros.
 *
 * Lee la definicion de `kpiDefinitions.js` y muestra un popover con:
 * - Que mide (descripcion)
 * - Como se calcula (formula)
 * - Como leerlo (thresholds con colores)
 *
 * Desktop: hover. Mobile: tap + overlay para cerrar.
 *
 * @param {{ kpiKey: string, size?: number }} props
 */
const KpiInfoTooltip = ({ kpiKey, size = 16 }) => {
  const def = KPI_DEFINITIONS[kpiKey];
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const hoverTimeout = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Calcular posicion del tooltip relativa al viewport
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 320;

    let top = rect.top + rect.height / 2;
    let left = rect.right + 8;

    // Si no hay espacio a la derecha, posicionar a la izquierda
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = rect.left - tooltipWidth - 8;
    }

    // Si no hay espacio a la izquierda, centrar abajo
    if (left < 16) {
      left = Math.max(16, rect.left - tooltipWidth / 2 + rect.width / 2);
      top = rect.bottom + 8;
    }

    // Ajustar vertical para no salir del viewport
    if (top + tooltipHeight > window.innerHeight - 16) {
      top = window.innerHeight - tooltipHeight - 16;
    }
    if (top < 16) top = 16;

    setPosition({ top, left });
  }, []);

  const handleMouseEnter = () => {
    if (isMobile) return;
    hoverTimeout.current = setTimeout(() => {
      updatePosition();
      setOpen(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    clearTimeout(hoverTimeout.current);
    setOpen(false);
  };

  const handleTap = (e) => {
    e.stopPropagation();
    if (!isMobile) return;
    if (open) {
      setOpen(false);
    } else {
      updatePosition();
      setOpen(true);
    }
  };

  // Cerrar al scrollear
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [open]);

  if (!def) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTap}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={`Informaci\u00f3n sobre ${def.name}`}
        style={styles.trigger}
      >
        <FaInfoCircle size={size} />
      </button>

      {open && (
        <>
          {/* Overlay mobile para cerrar al tap fuera */}
          {isMobile && (
            <div
              style={styles.mobileOverlay}
              onClick={() => setOpen(false)}
            />
          )}

          {/* Tooltip card */}
          <div
            ref={tooltipRef}
            role="tooltip"
            style={{
              ...styles.tooltipCard,
              ...(isMobile
                ? styles.tooltipMobile
                : { top: position.top, left: position.left }),
            }}
            onMouseEnter={isMobile ? undefined : () => clearTimeout(hoverTimeout.current)}
            onMouseLeave={isMobile ? undefined : () => setOpen(false)}
          >
            {/* Header */}
            <div style={styles.header}>
              <span style={styles.emoji}>{def.emoji}</span>
              <span style={styles.name}>{def.name}</span>
            </div>

            {/* Que mide */}
            <div style={styles.section}>
              <span style={styles.sectionTitle}>QUÉ MIDE</span>
              <p style={styles.sectionText}>{def.description}</p>
            </div>

            {/* Como se calcula */}
            <div style={styles.section}>
              <span style={styles.sectionTitle}>CÓMO SE CALCULA</span>
              <p style={styles.formulaText}>{def.formula}</p>
            </div>

            {/* Como leerlo */}
            <div style={styles.section}>
              <span style={styles.sectionTitle}>CÓMO LEERLO</span>
              <div style={styles.thresholdList}>
                {def.thresholds.map((t, i) => (
                  <div key={i} style={styles.thresholdRow}>
                    <span
                      style={{
                        ...styles.dot,
                        backgroundColor: getStatusColor(t.status),
                      }}
                    />
                    <span style={styles.thresholdText}>
                      {formatThresholdRange(def, t, i)}
                      {" \u2014 "}
                      {t.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

/**
 * Formatea el rango de un threshold en texto legible.
 */
function formatThresholdRange(def, threshold, index) {
  const thresholds = def.thresholds;
  const unit = def.unit || "";
  const prev = index > 0 ? thresholds[index - 1].max : null;

  if (threshold.max === Infinity) {
    const minVal = prev != null ? prev : 0;
    return `Mayor a ${minVal}${unit}`;
  }

  if (index === 0) {
    return `Menor a ${threshold.max}${unit}`;
  }

  return `${prev}\u2013${threshold.max}${unit}`;
}

// ─── Estilos ────────────────────────────────────────────────────

const styles = {
  trigger: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.gray400,
    transition: `color ${transitions.fast}`,
    borderRadius: radius.sm,
    lineHeight: 1,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  mobileOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.3)",
    zIndex: zIndex.dropdown,
  },

  tooltipCard: {
    position: "fixed",
    zIndex: zIndex.dropdown + 1,
    background: colors.bgCard,
    borderRadius: radius.md,
    boxShadow: shadows.lg,
    padding: spacing[4],
    maxWidth: 320,
    width: "max-content",
    maxHeight: "80vh",
    overflowY: "auto",
  },

  tooltipMobile: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "calc(100vw - 32px)",
    maxWidth: 360,
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    marginBottom: spacing[3],
    paddingBottom: spacing[2],
    borderBottom: `1px solid ${colors.gray100}`,
  },

  emoji: {
    fontSize: 18,
    lineHeight: 1,
  },

  name: {
    fontSize: typography.sizes.md,
    color: colors.black,
    fontWeight: 400,
  },

  section: {
    marginBottom: spacing[3],
  },

  sectionTitle: {
    fontSize: typography.sizes.xs,
    color: colors.gray600,
    letterSpacing: "0.5px",
    display: "block",
    marginBottom: spacing[1],
    fontWeight: 400,
  },

  sectionText: {
    fontSize: typography.sizes.base,
    color: colors.gray900,
    lineHeight: 1.5,
    margin: 0,
    fontWeight: 400,
  },

  formulaText: {
    fontSize: typography.sizes.sm,
    color: colors.gray700,
    lineHeight: 1.5,
    margin: 0,
    fontWeight: 400,
    fontFamily: "'SF Mono', 'Fira Code', monospace",
  },

  thresholdList: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
  },

  thresholdRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
  },

  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },

  thresholdText: {
    fontSize: typography.sizes.sm,
    color: colors.gray900,
    fontWeight: 400,
    lineHeight: 1.4,
  },
};

export default KpiInfoTooltip;
